import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Star, Map, Gauge, List,
  CheckCircle2, ArrowRight, PartyPopper, HandHeart, BellRing, ArchiveX,
  Loader2, Wrench, ChevronDown, ChevronUp, StickyNote, Sparkles,
} from "lucide-react";
import AppShell from "@/components/AppShell";
import StatusBadge from "@/components/StatusBadge";
import TimelineList from "@/components/TimelineList";
import HandleFlow from "@/components/action-modals/HandleFlow";
import HelpFlow from "@/components/action-modals/HelpFlow";
import RemindFlow from "@/components/action-modals/RemindFlow";
import { listRecommendations, markViewed, actionHandle, actionDismiss, completeRecommendation, getLatestBrief } from "@/lib/api";
import { briefCards as SAMPLE_CARDS } from "@/data/brief";
import { toast } from "sonner";

const iconFor = (name) => ({ star: Star, map: Map, gauge: Gauge, list: List }[name] || Star);

// Sample view uses static data so anonymous landing visitors always see 4 cards
// regardless of the live customer's state.
const buildSampleRecs = () =>
  SAMPLE_CARDS.map((c) => ({
    ...c,
    status: "new",
    createdAt: "2026-02-24T07:00:00Z",
    updatedAt: "2026-02-24T07:00:00Z",
    notes: [],
    attachments: [],
    timeline: [],
  }));

export default function MondayBrief({ isSample = false }) {
  const navigate = useNavigate();
  const [recs, setRecs] = useState(null); // null = loading
  const [briefHeader, setBriefHeader] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [authError, setAuthError] = useState(false);

  const [modal, setModal] = useState({ kind: null, reco: null });
  const openModal = (kind, reco) => setModal({ kind, reco });
  const closeModal = () => setModal({ kind: null, reco: null });

  const load = async () => {
    if (isSample) {
      setRecs(buildSampleRecs());
      return;
    }
    try {
      // Real customer path: NEVER auto-seed. Empty means empty.
      const [list, brief] = await Promise.all([
        listRecommendations(),
        getLatestBrief().catch(() => null),
      ]);
      setBriefHeader(brief || null);
      setRecs(list);
    } catch (err) {
      if (err?.response?.status === 401) {
        setAuthError(true);
        return;
      }
      setLoadError(err.message);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSample]);

  useEffect(() => {
    if (authError) navigate("/signup", { replace: true });
  }, [authError, navigate]);

  return (
    <AppShell showBack backTo={isSample ? "/" : "/dashboard"} testId="monday-brief-page">
      <div className="pt-4 sm:pt-6">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-widest font-medium" style={{ color: "var(--ta-secondary)" }} data-testid="brief-week-of">
            {isSample
              ? "Sample Monday Brief"
              : briefHeader?.weekStartDate
                ? `Monday Brief · ${new Date(briefHeader.weekStartDate).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}`
                : "Monday Brief"}
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mt-2" style={{ color: "var(--ta-primary)" }}>
            {isSample
              ? "This is what a real Monday Brief looks like."
              : briefHeader?.greeting
                ? briefHeader.greeting
                : "Your first Monday Brief."}
          </h1>
          <p className="mt-2 text-base" style={{ color: "var(--ta-text-secondary)" }} data-testid="brief-intro">
            {isSample
              ? "Placeholder data — but this is the whole rhythm."
              : briefHeader?.openingSummary
                ? briefHeader.openingSummary
                : "We're preparing your first Monday Brief. It will appear here after it has been generated."}
          </p>
        </div>

        {recs === null && !loadError && (
          <div className="flex items-center gap-2 text-sm" style={{ color: "var(--ta-text-secondary)" }} data-testid="brief-loading">
            <Loader2 size={16} className="animate-spin" /> Loading your brief…
          </div>
        )}

        {loadError && (
          <div className="p-5 rounded-2xl border" style={{ borderColor: "var(--ta-border)", background: "#FDECEA", color: "var(--ta-error)" }}>
            Couldn't load your brief. {loadError}
          </div>
        )}

        {recs && recs.length === 0 && (
          <EmptyState onGo={() => navigate(isSample ? "/" : "/dashboard")} isSample={isSample} hasBrief={!!briefHeader} />
        )}

        {recs && recs.length > 0 && (
          <div className="flex flex-col gap-4" data-testid="brief-card-list">
            {recs.map((r) => (
              <BriefCard
                key={r.id}
                reco={r}
                onOpenModal={openModal}
                onRefresh={load}
                isSample={isSample}
              />
            ))}
          </div>
        )}
      </div>

      <HandleFlow
        open={modal.kind === "handle"}
        onOpenChange={(v) => !v && closeModal()}
        recommendation={modal.reco}
        onCompleted={load}
      />
      <HelpFlow
        open={modal.kind === "help"}
        onOpenChange={(v) => !v && closeModal()}
        recommendation={modal.reco}
        onSubmitted={load}
      />
      <RemindFlow
        open={modal.kind === "remind"}
        onOpenChange={(v) => !v && closeModal()}
        recommendation={modal.reco}
        onSubmitted={load}
      />
    </AppShell>
  );
}

/* -------- Brief Card -------- */

function BriefCard({ reco, onOpenModal, onRefresh, isSample }) {
  const [viewed, setViewed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const Icon = iconFor(reco.icon);

  useEffect(() => {
    if (!viewed && !isSample) {
      markViewed(reco.id).catch(() => {});
      setViewed(true);
    }
  }, [reco.id, viewed, isSample]);

  const handlePositiveGotIt = async () => {
    try {
      await completeRecommendation(reco.id, { notes: "Acknowledged — thanks!", completedAt: new Date().toISOString() });
      toast.success("Enjoy it — you earned it.");
      onRefresh();
    } catch (err) {
      toast.error("Couldn't save that.");
    }
  };

  const handleDismiss = async () => {
    try {
      await actionDismiss(reco.id, "not-interested");
      toast("Hidden from future briefs.", { description: "We'll only bring this back if it becomes critical." });
      onRefresh();
    } catch (err) {
      toast.error("Couldn't hide it.");
    }
  };

  const handleTake = async () => {
    try {
      // Mark as in_progress immediately, then open the complete modal
      if (reco.status === "new") await actionHandle(reco.id);
      onOpenOrRefresh("handle");
    } catch (err) {
      toast.error("Something went wrong.");
    }
  };
  const onOpenOrRefresh = (kind) => {
    onOpenModal(kind, reco);
    // Refresh so status badge reflects handle instantly (optimistic)
    onRefresh();
  };

  const isPositive = reco.tone === "positive";
  const isComplete = reco.status === "completed";
  const isDismissed = reco.status === "dismissed"; // shouldn't render but safety
  const isWaiting = reco.status === "waiting_on_customer";
  const isInProgress = reco.status === "in_progress";

  return (
    <div
      className="page-fade rounded-[32px] bg-white p-6 sm:p-8 border shadow-sm"
      style={{ borderColor: isComplete ? "var(--ta-success)" : "var(--ta-border)" }}
      data-testid={`brief-card-${reco.id}`}
    >
      {/* Header row */}
      <div className="flex items-center gap-3 mb-4">
        <div
          className="inline-flex items-center justify-center w-11 h-11 rounded-2xl shrink-0"
          style={{
            background: isPositive ? "var(--ta-success-bg)" : "var(--ta-accent)",
            color: isPositive ? "var(--ta-success)" : "var(--ta-primary)",
          }}
        >
          <Icon size={22} strokeWidth={2.2} />
        </div>
        <div className="flex flex-col">
          <span className="text-[11px] uppercase tracking-widest font-medium" style={{ color: "var(--ta-secondary)" }}>{reco.tag}</span>
          <span className="text-xs" style={{ color: "var(--ta-text-secondary)" }}>Example: {reco.trade}</span>
        </div>
        <div className="ml-auto">
          <StatusBadge status={reco.status} testId={`status-${reco.id}`} size="sm" />
        </div>
      </div>

      <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight leading-snug" style={{ color: "var(--ta-primary)" }}>
        {reco.title}
      </h2>

      <div className="mt-6 flex flex-col gap-5">
        <Section label="What happened last week" text={reco.happened} />
        <Section label="Why it matters" text={reco.whyMatters} />
        <Section label="Recommendation" text={reco.recommendation} />
        <Section label="Business impact" text={reco.impact} />
      </div>

      {/* Status-aware footer */}
      <div className="mt-8" data-testid={`brief-footer-${reco.id}`}>
        {isDismissed ? null : isComplete ? (
          <CompleteBanner reco={reco} />
        ) : isWaiting ? (
          <WaitingBanner />
        ) : isInProgress ? (
          <InProgressBanner
            reco={reco}
            onMarkComplete={() => onOpenModal("handle", reco)}
          />
        ) : isPositive ? (
          <div className="flex flex-col gap-3">
            <div
              className="flex items-center gap-3 p-5 rounded-2xl"
              style={{ background: "var(--ta-success-bg)", color: "var(--ta-success)" }}
            >
              <CheckCircle2 size={22} strokeWidth={2.2} />
              <span className="font-medium">Nothing to do here — this one's a win.</span>
            </div>
            {!isSample && (
              <button
                onClick={handlePositiveGotIt}
                data-testid={`brief-got-it-${reco.id}`}
                className="w-full min-h-[56px] rounded-2xl text-base font-medium border-2 transition-transform hover:-translate-y-0.5"
                style={{ borderColor: "var(--ta-primary)", color: "var(--ta-primary)", background: "#fff" }}
              >
                Got it, thanks
              </button>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {!isSample && (
              <>
                <button
                  onClick={handleTake}
                  data-testid={`brief-take-btn-${reco.id}`}
                  className="w-full inline-flex items-center justify-center gap-2 min-h-[64px] rounded-2xl text-lg font-medium border-2 transition-transform hover:-translate-y-0.5"
                  style={{ borderColor: "var(--ta-primary)", color: "var(--ta-primary)", background: "#fff" }}
                >
                  <Wrench size={19} strokeWidth={2.2} /> I'll take care of it
                </button>
                <button
                  onClick={() => onOpenModal("help", reco)}
                  data-testid={`brief-help-btn-${reco.id}`}
                  className="w-full inline-flex items-center justify-center gap-2 min-h-[64px] rounded-2xl text-lg font-medium text-white transition-transform hover:-translate-y-0.5"
                  style={{ background: "var(--ta-secondary)" }}
                >
                  <HandHeart size={20} strokeWidth={2.2} /> Can you handle this for me?
                </button>
                <div className="flex gap-2 mt-1">
                  <button
                    onClick={() => onOpenModal("remind", reco)}
                    data-testid={`brief-remind-btn-${reco.id}`}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 min-h-[52px] rounded-2xl text-sm font-medium"
                    style={{ color: "var(--ta-primary)", background: "var(--ta-surface-alt)" }}
                  >
                    <BellRing size={16} /> Remind me later
                  </button>
                  <button
                    onClick={handleDismiss}
                    data-testid={`brief-dismiss-btn-${reco.id}`}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 min-h-[52px] rounded-2xl text-sm font-medium"
                    style={{ color: "var(--ta-text-secondary)", background: "var(--ta-surface-alt)" }}
                  >
                    <ArchiveX size={16} /> Not interested
                  </button>
                </div>
              </>
            )}
            {isSample && (
              <p className="text-sm italic" style={{ color: "var(--ta-text-secondary)" }}>
                Actions are disabled in this sample view.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Timeline & notes disclosure */}
      {reco.timeline?.length > 0 && !isSample && (
        <div className="mt-6 pt-6 border-t" style={{ borderColor: "var(--ta-border)" }}>
          <button
            onClick={() => setExpanded((v) => !v)}
            data-testid={`brief-history-toggle-${reco.id}`}
            className="w-full flex items-center justify-between text-sm font-medium"
            style={{ color: "var(--ta-primary)" }}
          >
            <span className="inline-flex items-center gap-2">
              <Sparkles size={14} />
              History ({reco.timeline.length} event{reco.timeline.length === 1 ? "" : "s"})
            </span>
            {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
          {expanded && (
            <div className="mt-4">
              <TimelineList events={reco.timeline} testId={`brief-timeline-${reco.id}`} />
              {reco.notes?.length > 0 && (
                <div className="mt-4 flex flex-col gap-2">
                  <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "var(--ta-secondary)" }}>
                    Notes
                  </div>
                  {reco.notes.map((n) => (
                    <div
                      key={n.id}
                      className="p-3 rounded-xl text-sm border-l-2"
                      style={{ background: "var(--ta-surface-alt)", borderColor: "var(--ta-primary)", color: "var(--ta-text)" }}
                      data-testid={`brief-note-${n.id}`}
                    >
                      <div className="flex items-center gap-2 mb-1 text-xs" style={{ color: "var(--ta-text-secondary)" }}>
                        <StickyNote size={12} /> {n.author?.name || "You"} · {new Date(n.createdAt).toLocaleDateString()}
                      </div>
                      {n.text}
                    </div>
                  ))}
                </div>
              )}
              {reco.attachments?.length > 0 && (
                <div className="mt-4">
                  <div className="text-[11px] uppercase tracking-widest font-semibold mb-2" style={{ color: "var(--ta-secondary)" }}>
                    Attached
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {reco.attachments.map((a) => (
                      <a
                        key={a.id}
                        href={a.data || a.futureStorageUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="block aspect-square rounded-xl overflow-hidden border"
                        style={{ borderColor: "var(--ta-border)" }}
                        data-testid={`brief-attachment-${a.id}`}
                      >
                        <img src={a.data || a.futureStorageUrl} alt={a.fileName} className="w-full h-full object-cover" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const Section = ({ label, text }) => (
  <div>
    <div className="text-[11px] uppercase tracking-widest font-semibold mb-2" style={{ color: "var(--ta-secondary)" }}>
      {label}
    </div>
    <p className="text-base sm:text-[17px] leading-relaxed" style={{ color: "var(--ta-text)" }}>
      {text}
    </p>
  </div>
);

const CompleteBanner = ({ reco }) => {
  const when = reco.completedAt ? new Date(reco.completedAt).toLocaleDateString(undefined, { month: "long", day: "numeric" }) : "";
  return (
    <div
      className="flex items-center gap-3 p-5 rounded-2xl"
      style={{ background: "var(--ta-success-bg)", color: "var(--ta-success)" }}
      data-testid={`complete-banner-${reco.id}`}
    >
      <CheckCircle2 size={22} strokeWidth={2.2} />
      <span className="font-medium">Marked complete{when ? ` on ${when}` : ""}. Nice work.</span>
    </div>
  );
};

const WaitingBanner = () => (
  <div
    className="flex items-center gap-3 p-5 rounded-2xl"
    style={{ background: "#FCE6DE", color: "var(--ta-secondary)" }}
    data-testid="waiting-banner"
  >
    <HandHeart size={22} strokeWidth={2.2} />
    <span className="font-medium">We've got it. An advisor will reach out today.</span>
  </div>
);

const InProgressBanner = ({ reco, onMarkComplete }) => (
  <div className="flex flex-col gap-3">
    <div
      className="flex items-center gap-3 p-5 rounded-2xl"
      style={{ background: "#FFF3D6", color: "#8A5A00" }}
      data-testid={`inprogress-banner-${reco.id}`}
    >
      <Wrench size={22} strokeWidth={2.2} />
      <span className="font-medium">You're on it. Come back when it's done.</span>
    </div>
    <button
      onClick={onMarkComplete}
      data-testid={`brief-mark-complete-${reco.id}`}
      className="w-full inline-flex items-center justify-center gap-2 min-h-[56px] rounded-2xl text-base font-medium text-white"
      style={{ background: "var(--ta-primary)" }}
    >
      <CheckCircle2 size={18} /> Mark complete
    </button>
  </div>
);

const EmptyState = ({ onGo, isSample, hasBrief }) => {
  const title = isSample
    ? "All caught up."
    : hasBrief
      ? "All caught up."
      : "We're preparing your first Monday Brief.";
  const body = isSample
    ? "Nothing needs your attention right now. Enjoy the quiet."
    : hasBrief
      ? "Nothing needs your attention right now. Enjoy the quiet."
      : "It will appear here after it has been generated. Your first brief lands next Monday at 7:00am.";
  return (
    <div className="pt-6 text-center max-w-md mx-auto" data-testid="brief-empty">
      <div
        className="mx-auto inline-flex items-center justify-center w-20 h-20 rounded-full mb-6"
        style={{ background: "var(--ta-success-bg)", color: "var(--ta-success)" }}
      >
        <PartyPopper size={36} strokeWidth={2.2} />
      </div>
      <h2 className="font-display text-3xl font-semibold tracking-tight" style={{ color: "var(--ta-primary)" }}>
        {title}
      </h2>
      <p className="mt-3 text-lg leading-relaxed" style={{ color: "var(--ta-text-secondary)" }}>
        {body}
      </p>
      <button
        onClick={onGo}
        data-testid="brief-empty-cta"
        className="mt-6 inline-flex items-center justify-center gap-2 min-h-[56px] px-6 rounded-2xl text-base font-medium text-white"
        style={{ background: "var(--ta-primary)" }}
      >
        {isSample ? "Back home" : "Back to dashboard"} <ArrowRight size={20} />
      </button>
    </div>
  );
};
